import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Paper, Tabs, Tab, Alert } from '@mui/material';
import Header from '../components/Header';
import DatasetUpload from '../components/DatasetUpload';
import DatasetBrowser from '../components/DatasetBrowser';
import { RENAISSANCE_COLORS } from '../utils';
import { toast } from 'react-toastify';
import apiService from '../services/api';

const Datasets: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedDataset, setSelectedDataset] = useState<string | undefined>(undefined);
  const [s3Configured, setS3Configured] = useState<boolean | null>(null);
  const [configMessage, setConfigMessage] = useState<string>('');
  const [loadingConfig, setLoadingConfig] = useState(true);

  useEffect(() => {
    const checkConfig = async () => {
      try {
        const config = await apiService.checkDatasetsConfig();
        setS3Configured(config.configured);
        setConfigMessage(config.message);
      } catch (err: any) {
        setS3Configured(false);
        setConfigMessage('Unable to check S3 configuration. Please ensure the backend is running.');
      } finally {
        setLoadingConfig(false);
      }
    };
    checkConfig();
  }, []);

  const handleUploadComplete = (s3Key: string) => {
    // Switch to browser tab after upload
    setTabValue(1);
    toast.info('Upload complete! You can now view and select the dataset.');
  };

  const handleDatasetSelect = (s3Key: string) => {
    setSelectedDataset(s3Key);
    toast.info(`Dataset selected: ${s3Key.split('/').pop()}`);
  };

  return (
    <div style={{
      backgroundColor: RENAISSANCE_COLORS.beige,
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    }}>
      <Header />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main' }}>
            Dataset Management
          </Typography>

          <Typography variant="body1" paragraph>
            Upload CSV files to S3 or browse existing datasets. Uploaded CSV files are automatically
            converted to Parquet format for efficient processing.
          </Typography>

          {loadingConfig ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Checking configuration...
            </Alert>
          ) : !s3Configured ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                S3 Dataset Storage Not Configured
              </Typography>
              <Typography variant="body2">
                {configMessage}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                To enable dataset management, set the <code>S3_DATASETS_BUCKET</code> environment variable
                in your backend configuration. This should be the name of an S3 bucket that has been
                provisioned for storing datasets.
              </Typography>
            </Alert>
          ) : (
            <Box sx={{ mt: 3 }}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
                <Tab label="Upload Dataset" />
                <Tab label="Browse Datasets" />
              </Tabs>

              {tabValue === 0 && (
                <DatasetUpload onUploadComplete={handleUploadComplete} />
              )}

              {tabValue === 1 && (
                <DatasetBrowser
                  onDatasetSelect={handleDatasetSelect}
                  selectedDataset={selectedDataset}
                />
              )}
            </Box>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default Datasets;
